import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_PRODUCT_LINE,
  PRODUCT_LINE_STORAGE_KEY,
  productLineForPath,
  type ProductLine,
} from "@/platform/productLine";

// Holds the active platform (Commercial | Surety) for the whole app. Mounted in
// RootLayout so both the top-bar switcher and the left nav read one source, and
// so it wraps every route the shells render.
//
// BRK-S090: the line is DERIVED from the current route — there is no setter, and
// therefore no way for the pill and the page to disagree. The switcher just
// navigates to the chosen platform's home. localStorage carries the last derived
// line so the line-agnostic routes (/profile, /security, Accounts) keep showing
// the platform the broker came from, and a returning broker reopens there;
// a route that names a platform always outranks it.

interface ProductLineContextValue {
  productLine: ProductLine;
}

const ProductLineContext = createContext<ProductLineContextValue | null>(null);

/** Read the persisted line, guarded — a blocked or absent localStorage must
 *  never white-screen the app (falls back to the default). */
function readStoredLine(): ProductLine {
  try {
    const raw = window.localStorage.getItem(PRODUCT_LINE_STORAGE_KEY);
    if (raw === "commercial" || raw === "surety") return raw;
  } catch {
    /* localStorage unavailable — use the default */
  }
  return DEFAULT_PRODUCT_LINE;
}

export function ProductLineProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  // Only consulted where the route is silent about the platform.
  const [fallbackLine, setFallbackLine] = useState<ProductLine>(readStoredLine);
  const routeLine = productLineForPath(pathname);
  const productLine = routeLine ?? fallbackLine;

  useEffect(() => {
    setFallbackLine(productLine);
    try {
      window.localStorage.setItem(PRODUCT_LINE_STORAGE_KEY, productLine);
    } catch {
      /* best-effort persistence — ignore if storage is unavailable */
    }
  }, [productLine]);

  const value = useMemo(() => ({ productLine }), [productLine]);

  return (
    <ProductLineContext.Provider value={value}>
      {children}
    </ProductLineContext.Provider>
  );
}

/** Access the active platform. Throws if used outside the provider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useProductLine(): ProductLineContextValue {
  const ctx = useContext(ProductLineContext);
  if (ctx === null) {
    throw new Error("useProductLine must be used within a ProductLineProvider");
  }
  return ctx;
}
